import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {SUPPORTED_LANGUAGE_CODES, DEFAULT_LANGUAGE} from '../locales/languageConfig';

/**
 * Extrait le texte d'un objet multilingue plat (`{fr, en, nl, de, ...}`).
 *
 * Adapté aux schémas qui stockent directement chaque langue comme clé
 * racine (ex. `formula.name.fr`), par opposition à `getTranslatedField`
 * qui cible le schéma imbriqué `item.translations[field][lang]` des
 * bons cadeaux.
 *
 * Stratégie de résolution :
 * 1. `options.lang` si fourni et non vide dans l'objet ;
 * 2. `DEFAULT_LANGUAGE` si non vide ;
 * 3. Première langue non vide parmi `SUPPORTED_LANGUAGE_CODES` ;
 * 4. `fallback` si rien d'exploitable.
 *
 * Si l'argument est déjà une chaîne (legacy), elle est retournée telle
 * quelle pour préserver la rétrocompatibilité.
 *
 * Le paramètre `options` accepte deux formes pour la rétrocompatibilité :
 * - string : traité comme le fallback historique
 * - objet `{lang, fallback}` : langue prioritaire + fallback
 *
 * @param {Object|string|null|undefined} value - Objet multilingue ou chaîne directe
 * @param {Object|string} [options] - Options ou fallback (legacy)
 * @param {string} [options.lang] - Code langue prioritaire (ex. langue courante i18n)
 * @param {string} [options.fallback=''] - Valeur retournée si rien n'est exploitable
 * @return {string} Texte localisé ou fallback
 *
 * @example
 * getLocalizedText({fr: 'Bonjour', en: 'Hello'}, {lang: 'en'}) // 'Hello'
 * getLocalizedText({en: 'Hello', nl: 'Hallo'}, {lang: 'fr'})   // 'Hello' (fallback DEFAULT_LANGUAGE puis premier non vide)
 * getLocalizedText(null, 'Formule')                            // 'Formule'
 * getLocalizedText('Texte brut')                               // 'Texte brut'
 */
export function getLocalizedText(value, options = '') {
  const {lang, fallback} = typeof options === 'string' ?
    {lang: undefined, fallback: options} :
    {lang: options?.lang, fallback: options?.fallback ?? ''};

  if (!value) return fallback;
  if (typeof value === 'string') return value || fallback;

  if (lang && value[lang]) return value[lang];
  if (value[DEFAULT_LANGUAGE]) return value[DEFAULT_LANGUAGE];

  for (const code of SUPPORTED_LANGUAGE_CODES) {
    if (value[code]) return value[code];
  }

  return fallback;
}

/**
 * Hook renvoyant une fonction `getLocalizedText` pré-configurée avec la langue
 * courante de l'utilisateur (i18n). Pratique dans les composants React pour
 * éviter de passer manuellement la langue à chaque appel.
 *
 * La fonction retournée est mémoïsée sur la langue courante : sa référence
 * reste stable entre rendus tant que la langue ne change pas, ce qui permet
 * de l'inclure sans danger dans les deps d'un `useEffect`/`useCallback`.
 *
 * @return {Function} Fonction `(value, fallback?) => string` mémoïsée sur la langue courante.
 *
 * @example
 * const localize = useLocalizedText();
 * const name = localize(formula.name, 'Formule');
 */
export const useLocalizedText = () => {
  const {i18n} = useTranslation();
  const lang = i18n?.language || DEFAULT_LANGUAGE;
  return useCallback(
      (value, fallback = '') => getLocalizedText(value, {lang, fallback}),
      [lang],
  );
};

/**
 * Construit un objet multilingue vide pour toutes les langues supportées.
 *
 * Source de vérité : `SUPPORTED_LANGUAGE_CODES` de `locales/languageConfig`.
 * Pratique comme valeur initiale de formulaire React Hook Form pour les
 * champs multilingues plats (`{fr: '', en: '', nl: '', de: ''}`).
 *
 * @return {Object} Objet `{lang: ''}` pour chaque langue supportée.
 */
export const buildEmptyMultilingual = () =>
  SUPPORTED_LANGUAGE_CODES.reduce((acc, lang) => {
    acc[lang] = '';
    return acc;
  }, {});

/**
 * Normalise un objet multilingue source vers un objet contenant toutes les
 * langues supportées (chaîne vide pour les langues absentes ou nulles).
 *
 * Utile à l'édition d'un document existant (ex. formule) pour pré-remplir le
 * formulaire avec une structure stable, indépendamment des langues présentes
 * dans le document source.
 *
 * @param {Object} [source] - Objet multilingue source (peut être partiel)
 * @return {Object} Objet multilingue normalisé `{fr: ..., en: ..., ...}`
 */
export const pickMultilingual = (source) =>
  SUPPORTED_LANGUAGE_CODES.reduce((acc, lang) => {
    acc[lang] = source?.[lang] || '';
    return acc;
  }, {});

/**
 * Utilitaire pour gérer les traductions des bons cadeaux
 *
 * Cette fonction récupère le texte approprié selon la langue courante de l'utilisateur,
 * avec un fallback sur les champs principaux si les traductions ne sont pas disponibles.
 *
 * @param {Object} item - L'objet contenant les données (voucher, category, option)
 * @param {string} fieldName - Le nom du champ à traduire ('name', 'description', 'conditions')
 * @param {string} currentLanguage - La langue courante de l'utilisateur
 * @return {string} Le texte traduit ou le fallback approprié
 */
export const getTranslatedField = (item, fieldName, currentLanguage = 'fr') => {
  // Si l'item n'existe pas, retourner une chaîne vide
  if (!item) return '';

  // Si les traductions existent et contiennent le champ demandé
  if (item.translations &&
      item.translations[fieldName] &&
      item.translations[fieldName][currentLanguage]) {
    const translatedText = item.translations[fieldName][currentLanguage];
    // Vérifier que la traduction n'est pas vide
    if (translatedText && translatedText.trim() !== '') {
      return translatedText;
    }
  }

  // Fallback sur le champ principal
  return item[fieldName] || '';
};

/**
 * Hook personnalisé pour utiliser les traductions des vouchers
 *
 * @param {Object} companyDetails - Détails de l'entreprise (optionnel)
 * @return {Object} Objet contenant les fonctions de traduction
 */
export const useVoucherTranslation = (companyDetails = null) => {
  const {i18n} = useTranslation();
  const currentLanguage = i18n.language || 'fr';

  // Vérifier si le multilingue est activé pour cette entreprise
  const isMultiLanguageEnabled = companyDetails?.multiLanguage?.enabled || false;

  /**
   * Retrieves content while respecting the multilingual configuration.
   *
   * @param {Object} item - The object containing the data (voucher, category, option).
   * @param {string} fieldName - The name of the field to translate ('name', 'description', 'conditions').
   * @return {string} The translated content or the fallback value.
   */
  const getTranslatedContent = (item, fieldName) => {
    if (!item) return '';

    // Si multilingue désactivé, retourne toujours le champ original
    if (!isMultiLanguageEnabled) {
      return item[fieldName] || '';
    }

    // Sinon utilise la logique de traduction normale
    return getTranslatedField(item, fieldName, currentLanguage);
  };

  /**
   * Récupère le nom traduit d'un élément
   *
   * @param {Object} item - L'objet contenant les données (voucher, category, option)
   * @return {string} Le nom traduit ou le fallback approprié
   */
  const getTranslatedName = (item) => getTranslatedContent(item, 'name');

  /**
   * Récupère la description traduite d'un élément
   *
   * @param {Object} item - L'objet contenant les données (voucher, category, option)
   * @return {string} La description traduite ou le fallback approprié
   */
  const getTranslatedDescription = (item) => getTranslatedContent(item, 'description');

  /**
   * Récupère les conditions traduites d'un élément
   *
   * @param {Object} item - L'objet contenant les données (voucher, category, option)
   * @return {string} Les conditions traduites ou le fallback approprié
   */
  const getTranslatedConditions = (item) => getTranslatedContent(item, 'conditions');

  return {
    currentLanguage,
    isMultiLanguageEnabled,
    getTranslatedName,
    getTranslatedDescription,
    getTranslatedConditions,
    getTranslatedField: (item, fieldName) => getTranslatedContent(item, fieldName),
  };
};
